/**
 * GDPR/DSGVO Consent Management Utilities
 * Provides centralized consent management with audit trail and legal compliance features
 */

import { CookieConsent, CookieConsentData } from '@/types'
import { CONSENT_CONFIG, generateConsentAuditData } from './consent-config'

/**
 * Consent Manager Class - handles all consent operations
 */
export class ConsentManager {
  private static instance: ConsentManager
  private consentData: CookieConsentData | null = null
  private listeners: Set<(consent: CookieConsent) => void> = new Set()

  private constructor() {
    // Initialize consent data from storage
    this.loadConsentData()
  }

  public static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager()
    }
    return ConsentManager.instance
  }

  /**
   * Load consent data from localStorage/cookie
   */
  private loadConsentData(): void {
    if (typeof window === 'undefined') return

    try {
      // Try localStorage first
      const stored = localStorage.getItem(CONSENT_CONFIG.COOKIE_NAME)
      if (stored) {
        const data = JSON.parse(stored)
        if (this.isConsentValid(data)) {
          this.consentData = data
          return
        }
      }

      // Fallback to cookie
      const cookieValue = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${CONSENT_CONFIG.COOKIE_NAME}=`))
        ?.split('=')[1]

      if (cookieValue) {
        const data = JSON.parse(decodeURIComponent(cookieValue))
        if (this.isConsentValid(data)) {
          this.consentData = data
          return
        }
      }
    } catch (error) {
      console.error('Error loading consent data:', error)
    }

    // No valid consent found
    this.consentData = null
  }

  /**
   * Validate consent data structure and expiration
   */
  private isConsentValid(data: any): data is CookieConsentData {
    if (!data?.timestamp || !data?.version || !data?.preferences) {
      return false
    }

    // Check version compatibility
    if (data.version !== CONSENT_CONFIG.VERSION) {
      return false
    }

    // Check expiration
    const consentDate = new Date(data.timestamp)
    const expirationDate = new Date(
      consentDate.getTime() + CONSENT_CONFIG.EXPIRATION_DAYS * 24 * 60 * 60 * 1000
    )

    return new Date() < expirationDate
  }

  /**
   * Save consent preferences with audit trail
   */
  public saveConsent(preferences: CookieConsent): void {
    const auditData = generateConsentAuditData()

    this.consentData = {
      preferences,
      timestamp: new Date().toISOString(),
      version: CONSENT_CONFIG.VERSION,
      consentId: auditData.consentId,
      // Additional audit fields could be added here
      // ipHash: await hashIP(userIP), // if IP tracking is needed for audit
      // userAgent: navigator.userAgent.substring(0, 200), // truncated for privacy
    }

    // Save to localStorage (primary storage)
    try {
      localStorage.setItem(CONSENT_CONFIG.COOKIE_NAME, JSON.stringify(this.consentData))
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }

    // Save to cookie as fallback
    try {
      const expirationDate = new Date()
      expirationDate.setDate(expirationDate.getDate() + CONSENT_CONFIG.EXPIRATION_DAYS)

      document.cookie = `${CONSENT_CONFIG.COOKIE_NAME}=${encodeURIComponent(
        JSON.stringify(this.consentData)
      )}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Strict; Secure`
    } catch (error) {
      console.error('Error saving consent cookie:', error)
    }

    // Log for audit trail (in production, consider sending to audit service)
    this.logConsentAction('consent_saved', preferences)

    // Notify listeners
    this.notifyListeners(preferences)

    // Dispatch browser event for other components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('cookieConsentChange', {
          detail: preferences,
        })
      )
    }
  }

  /**
   * Get current consent preferences
   */
  public getConsent(): CookieConsent | null {
    return this.consentData?.preferences || null
  }

  /**
   * Check if consent is required (no valid consent exists)
   */
  public isConsentRequired(): boolean {
    return this.consentData === null
  }

  /**
   * Check if specific category has consent
   */
  public hasConsent(category: keyof CookieConsent): boolean {
    const consent = this.getConsent()
    return consent?.[category] ?? false
  }

  /**
   * Withdraw all non-essential consent
   */
  public withdrawConsent(): void {
    const minimalConsent: CookieConsent = {
      essential: true,
      analytics: false,
      preferences: false,
      marketing: false,
    }

    this.saveConsent(minimalConsent)
    this.logConsentAction('consent_withdrawn', minimalConsent)
  }

  /**
   * Subscribe to consent changes
   */
  public subscribe(callback: (consent: CookieConsent) => void): () => void {
    this.listeners.add(callback)

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback)
    }
  }

  /**
   * Notify all listeners of consent changes
   */
  private notifyListeners(consent: CookieConsent): void {
    this.listeners.forEach((callback) => {
      try {
        callback(consent)
      } catch (error) {
        console.error('Error in consent listener:', error)
      }
    })
  }

  /**
   * Log consent actions for audit trail
   */
  private logConsentAction(action: string, preferences: CookieConsent | null): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('Consent Action:', {
        action,
        preferences,
        timestamp: new Date().toISOString(),
        consentId: this.consentData?.consentId,
      })
    }

    // In production, you might want to send audit logs to a secure endpoint
    // for compliance documentation and legal requirements
    /*
        if (process.env.NODE_ENV === 'production') {
          fetch('/api/consent-audit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action,
              preferences,
              timestamp: new Date().toISOString(),
              consentId: this.consentData?.consentId,
              // Additional audit fields as needed
            })
          }).catch(error => console.error('Audit log failed:', error))
        }
        */
  }

  /**
   * Get consent history summary (for user transparency)
   */
  public getConsentSummary(): {
    consentId: string | null
    timestamp: string | null
    version: string | null
    hasAnalytics: boolean
    hasMarketing: boolean
    hasPreferences: boolean
  } {
    const data = this.consentData
    const consent = data?.preferences

    return {
      consentId: data?.consentId || null,
      timestamp: data?.timestamp || null,
      version: data?.version || null,
      hasAnalytics: consent?.analytics || false,
      hasMarketing: false,
      hasPreferences: consent?.preferences || false,
    }
  }
}

// Export singleton instance
export const consentManager = ConsentManager.getInstance()

// Open cookie settings modal
export function openCookieSettings(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('openCookieSettings'))
  }
}

// React hook for consent management
export function useConsent() {
  if (typeof window === 'undefined') {
    return {
      consent: null,
      hasConsent: () => false,
      saveConsent: () => {},
      withdrawConsent: () => {},
      isConsentRequired: () => true,
      openSettings: () => {},
    }
  }

  const manager = consentManager

  return {
    consent: manager.getConsent(),
    hasConsent: (category: keyof CookieConsent) => manager.hasConsent(category),
    saveConsent: (preferences: CookieConsent) => manager.saveConsent(preferences),
    withdrawConsent: () => manager.withdrawConsent(),
    isConsentRequired: () => manager.isConsentRequired(),
    getConsentSummary: () => manager.getConsentSummary(),
    openSettings: openCookieSettings,
  }
}
